import { useCallback, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import {
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Skeleton,
  Typography,
  Upload,
  theme,
} from "antd";
import type { UploadFile } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVideo,
  faPlus,
  faPen,
  faTrash,
  faUpload,
  faFilm,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../contexts/auth";
import { useToasts } from "../../contexts/toasts";
import { Roles } from "../../enums/Roles";
import { Video, TutorialVideo } from "../../api/Video";

const { Title, Text, Paragraph } = Typography;

export default function VideoTutorialPage() {
  const { loggedUser } = useAuth();
  const { showToast } = useToasts();
  const { token } = theme.useToken();
  const [form] = Form.useForm();

  const isManager =
    loggedUser?.role === Roles.Superadmin || loggedUser?.role === Roles.Trainer;

  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState<TutorialVideo[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TutorialVideo | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Video.list();
      setVideos(res.data.list ?? []);
    } catch (_) {
      showToast("video-load-err", "Errore nel caricamento dei video", "", "danger");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setFileList([]);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (v: TutorialVideo) => {
    setEditing(v);
    setFileList([]);
    form.setFieldsValue({ title: v.title, description: v.description ?? "" });
    setModalOpen(true);
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        await Video.update(editing.id, {
          title: values.title,
          description: values.description,
        });
        showToast("video-upd", "Video aggiornato", "", "success");
      } else {
        const file = fileList[0]?.originFileObj;
        if (!file) {
          showToast("video-nofile", "Seleziona un file video", "", "warning");
          setSaving(false);
          return;
        }
        const fd = new FormData();
        fd.append("video", file);
        fd.append("title", values.title);
        if (values.description) fd.append("description", values.description);
        await Video.create(fd);
        showToast("video-new", "Video caricato", "", "success");
      }
      setModalOpen(false);
      load();
    } catch (err: any) {
      if (err?.errorFields) return; // form validation error
      showToast("video-save-err", "Errore nel salvataggio del video", "", "danger");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v: TutorialVideo) => {
    try {
      await Video.remove(v.id);
      setVideos((prev) => prev.filter((x) => x.id !== v.id));
      showToast("video-del", "Video eliminato", "", "success");
    } catch (_) {
      showToast("video-del-err", "Errore nell'eliminazione", "", "danger");
    }
  };

  return (
    <Container fluid="lg" className="my-3">
      <Helmet>
        <title>Video Tutorial</title>
      </Helmet>

      <Flex justify="space-between" align="center" wrap="wrap" gap={12} className="mb-3">
        <div>
          <Title level={2} className="mb-0">
            <FontAwesomeIcon icon={faVideo} className="me-2" style={{ color: token.colorPrimary }} />
            Video Tutorial
          </Title>
          <Text type="secondary">
            {isManager
              ? "Carica e gestisci i video per i tuoi atleti."
              : "I video condivisi dal tuo trainer."}
          </Text>
        </div>
        {isManager && (
          <Button
            className="cm-btn cm-btn--primary"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={openCreate}
          >
            Nuovo video
          </Button>
        )}
      </Flex>

      {loading ? (
        <Row gutter={[16, 16]}>
          {[0, 1, 2].map((i) => (
            <Col xs={24} sm={12} lg={8} key={i}>
              <Card>
                <Skeleton active paragraph={{ rows: 3 }} />
              </Card>
            </Col>
          ))}
        </Row>
      ) : videos.length === 0 ? (
        <Empty
          className="mt-5"
          description={isManager ? "Non hai ancora caricato video" : "Nessun video disponibile"}
        />
      ) : (
        <Row gutter={[16, 16]}>
          {videos.map((v) => (
            <Col xs={24} sm={12} lg={8} key={v.id}>
              <Card
                className="h-100"
                cover={
                  v.signedUrl ? (
                    <video
                      src={v.signedUrl}
                      controls
                      preload="metadata"
                      style={{ width: "100%", maxHeight: 220, background: "#000", borderRadius: "8px 8px 0 0" }}
                    />
                  ) : (
                    <Flex
                      align="center"
                      justify="center"
                      style={{ height: 180, background: token.colorFillSecondary }}
                    >
                      <FontAwesomeIcon icon={faFilm} style={{ fontSize: 40, color: token.colorTextTertiary }} />
                    </Flex>
                  )
                }
                actions={
                  isManager
                    ? [
                        <Button type="text" icon={<FontAwesomeIcon icon={faPen} />} onClick={() => openEdit(v)} key="edit">
                          Modifica
                        </Button>,
                        <Popconfirm
                          key="del"
                          title="Eliminare il video?"
                          okText="Elimina"
                          okButtonProps={{ danger: true }}
                          cancelText="Annulla"
                          onConfirm={() => remove(v)}
                        >
                          <Button type="text" danger icon={<FontAwesomeIcon icon={faTrash} />}>
                            Elimina
                          </Button>
                        </Popconfirm>,
                      ]
                    : undefined
                }
              >
                <Card.Meta
                  title={v.title}
                  description={
                    v.description ? (
                      <Paragraph type="secondary" ellipsis={{ rows: 2 }} className="mb-0">
                        {v.description}
                      </Paragraph>
                    ) : null
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal
        title={editing ? "Modifica video" : "Nuovo video"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        okText={editing ? "Salva" : "Carica"}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" className="mt-3">
          <Form.Item
            name="title"
            label="Titolo"
            rules={[{ required: true, message: "Il titolo è obbligatorio" }]}
          >
            <Input placeholder="Titolo del video" />
          </Form.Item>
          <Form.Item name="description" label="Descrizione">
            <Input.TextArea rows={3} placeholder="Descrizione (opzionale)" />
          </Form.Item>
          {!editing && (
            <Form.Item label="File video" required>
              <Upload
                accept="video/*"
                maxCount={1}
                beforeUpload={() => false}
                fileList={fileList}
                onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
              >
                <Button icon={<FontAwesomeIcon icon={faUpload} />}>Seleziona video</Button>
              </Upload>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Formati: mp4, mov, avi, mkv — max 2GB.
              </Text>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Container>
  );
}
